import React, { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Row, Col, Card, Button, Spinner, Form } from "react-bootstrap";
import PageTitle from "../../Components/PageTitle";
import { useTranslation } from "react-i18next";
import { getCityListById, saveCityData, updateCityData } from "../../redux/actions/CityActions";
import { getAllStateList } from "../../redux/actions/MasterActions";
import FormInput from "../../Components/FormInput";
import { useNavigate, useParams } from "react-router-dom";
import { logout } from "../../redux/actions/AuthActions";
import { toast } from "react-toastify";
import Select from "react-dropdown-select";
import swal from 'sweetalert2';
import Preloader from "../../Components/Preloader";

const Dashboard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({});
  const dispatch = useDispatch();
  const {
    AuthReducer: { auth, loading, errors },
    cityReducer: {
      loadingCity,
      errorsCity,
      cityLists,
      changedCityStatus,
    },
    masterReducer: {
      masterStateLists,
      loadingMaster,
      errorsMaster,
    },
  } = useSelector((state) => state);

  useEffect(() => {
    if (auth?.accessToken) {
      dispatch(getAllStateList(auth?.accessToken));
    }
  }, [auth?.accessToken]);

  useEffect(() => {
    if (id) {
      dispatch(getCityListById(auth?.accessToken, id));
    }
  }, [auth, id]);


  const [validated, setValidated] = useState(false);

  const handleSubmit = (event) => {
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
    }
    setValidated(true);
    if (id) {
      dispatch(updateCityData(auth?.accessToken, formData, id));
    }
    else {
      dispatch(saveCityData(auth?.accessToken, formData));
setValidated(false);
    }
  };

  useEffect(() => {
    if (id) {
      if (cityLists && Object.keys(cityLists).length > 0) {
        setFormData({
          nicCityCode: cityLists?.nicCityCode,
          cityName: cityLists?.cityName,
          stateId: cityLists?.state?.id,
        });
      }
    }

  }, [cityLists])

  useEffect(() => {
    if (errors && Object.keys(errors).length > 0) {
      if (errors?.status === 401) {
        dispatch(logout());
        toast.error(errors?.data?.message);
        navigate('/login');
        window.location.reload();
      }
    }
  }, [errors, dispatch, navigate]);

  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (errorsCity && Object.keys(errorsCity).length > 0) {
      if (errorsCity?.status == 401) {
        dispatch(logout())
        toast.error(errors?.data?.message);
        navigate('/login');
        //window.location.reload();
      }
      else if (errorsCity?.status === 409) {
        swal.fire({
          show: true,
          title: "Oops!",
          text: errorsCity?.data?.message,
          icon: "error",
          showConfirmButton: false,
          timer: 1500
        });
        dispatch({ type: 'RESET_DELETE_CITY_STATUS' });
      } else if (errorsCity?.status === 400) {
        // Transform errors array to field-based object
        if (errorsCity?.data?.errors && Array.isArray(errorsCity.data.errors)) {
          const transformedErrors = {};
          errorsCity.data.errors.forEach(error => {
            if (error.field && error.message) {
              transformedErrors[error.field] = error.message;
            }
          });
          setFieldErrors(transformedErrors);
        }
      } else {
        dispatch({ type: 'RESET_DELETE_CITY_STATUS' });
      }
    }
  }, [errorsCity, dispatch]);

  useEffect(() => {
    if (errorsMaster && Object.keys(errorsMaster).length > 0) {
      if (errorsMaster?.status == 401) {
        dispatch(logout())
        toast.error(errors?.data?.message);
        navigate('/login');
      }
    }
  }, [errorsMaster]);

  useEffect(() => {
    if (changedCityStatus?.message) {
      swal.fire({
        show: true,
        title: "Well done!",
        text: changedCityStatus?.message,
        icon: "success",
        showConfirmButton: false,
        timer: 1500
      });
      dispatch({ type: 'RESET_DELETE_CITY_STATUS' });
      navigate(-1);
    }
  }, [changedCityStatus, dispatch, navigate]);

  useEffect(() => {
    setFormData({});
    dispatch({ type: 'RESET_DELETE_CITY_STATUS' });
  }, [dispatch]);

  useEffect(() => {
    setFieldErrors({});
},[(navigate)]);

  const inputHandler = (e) => {
    const { value, name } = e.target;
    setFormData({ ...formData, [name]: value });
    if (fieldErrors?.[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const inputHandlerCaps = (e) => {
    const { value, name } = e.target;
    const processedValue = value.replace(/\s+/g, '').toUpperCase(); // Remove spaces and convert to uppercase
    setFormData({ ...formData, [name]: processedValue });
    if (fieldErrors?.[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const selectHandler = (selectedData, name) => {
    setFormData(prevFormData => ({
      ...prevFormData,
      [name]: selectedData[0]?.stateId
    }));
    if (fieldErrors?.[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: errors?.data?.stateId ? 'red' : base.borderColor,
      '&:hover': {
        borderColor: errors?.data?.stateId ? 'red' : base.borderColor,
      }
    }),
  };

  return (
    <>
      <PageTitle
        breadCrumbItems={[
          { label: t("City"), path: "City" },
          { label: id ? t("Update City") : t("Add City"), path: "/Add-City", active: true },
        ]}
        title={id ? t("Update City") : t("Add City")}
      />

      <Row>
        <Col lg={4}></Col>
        <Col lg={4}>
          <Card>
            <Card.Body>
              {errorsCity?.data?.message && (
                <div className="alert alert-danger">
                  {errorsCity?.data?.message}
                </div>
              )}

              <Form.Group className="mb-3">
                <Form.Label>{t("State")}</Form.Label>
                <Select
                    className={fieldErrors?.stateId ? "border-danger" : ""}
                  options={masterStateLists}
                  values={masterStateLists.filter((state) => state.stateId === formData.stateId)}
                  name="stateId"
                  labelField={"stateName"}
                  valueField={"stateId"}
                  onChange={(selectedData) => selectHandler(selectedData, "stateId")}
                  styles={customSelectStyles}
                />

                {errorsCity?.data?.stateId && (
                  <div className="invalid-feedback d-block">
                    <p className="text-danger">{errorsCity?.data?.stateId}</p>
                  </div>
                )}
              </Form.Group>

              <FormInput
                label={t("City Code")}
                type="text"
                errors={fieldErrors}
                value={formData.nicCityCode}
                name="nicCityCode"
                onChange={inputHandlerCaps}
                containerClass={"mb-3"}
              />

              <FormInput
                label={t("City Name")}
                type="text"
                errors={fieldErrors}
                value={formData.cityName}
                name="cityName"
                onChange={inputHandler}
                containerClass={"mb-3"}
              />

              <div className="d-flex justify-content-center">
                {loading ?
                  <Button color="primary" disabled>
                    <Spinner
                      className="spinner-border-sm me-1"
                      tag="span"
                      color="white"
                    />
                    Please wait ...
                  </Button>
                  :
                  <Button type="button" onClick={handleSubmit} disabled={loading}>
                    Submit
                  </Button>
                }
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {loadingCity && (<Preloader />)}
      {loadingMaster && (<Preloader />)}
    </>
  );
};

export default Dashboard;
